import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import RichTextEditor from "../../../components/RichTextEditor";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  ReorderButtons,
} from "../../../components/FormField";

import { moveItem } from "./shared";

// ─── Single Terms Section Block — defined OUTSIDE to prevent remount ──────────
function TermsBlock({ sec, idx, total, onChange, onMove, onRemove }) {
  const [open, setOpen] = useState(idx === 0);

  return (
    <div className="border rounded-xl mb-3 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-muted border-b border-border">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 text-left min-w-0"
        >
          {open ? (
            <ChevronUp size={14} className="shrink-0" />
          ) : (
            <ChevronDown size={14} className="shrink-0" />
          )}
          <p className="text-xs font-semibold truncate">
            {sec.title || `Section ${idx + 1}`}
          </p>
        </button>
        <div className="flex gap-2 items-center shrink-0 ml-2">
          <ReorderButtons index={idx} total={total} onMove={onMove} />
          {total > 1 && (
            <button onClick={onRemove}>
              <Trash2 size={14} className="text-destructive" />
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="p-4 space-y-3">
          <Field label="Section Title">
            <TextInput
              value={sec.title}
              placeholder="e.g. 1. Acceptance of Terms"
              onChange={(e) => onChange({ ...sec, title: e.target.value })}
            />
          </Field>
          <Field label="Content">
            <div className="border border-border rounded-lg overflow-hidden">
              <RichTextEditor
                value={sec.content}
                onChange={(data) => onChange({ ...sec, content: data })}
                placeholder="Write your full Policy content here..."
              />
            </div>
          </Field>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function TermsSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Legal");
  const [heroHeading, setHeroHeading] = useState("Terms &");
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState("Conditions");
  const [heroLastUpdated, setHeroLastUpdated] = useState("1 January 2025");
  const [heroIntro, setHeroIntro] = useState("");

  /* ---------------- TERMS SECTIONS ---------------- */
  const [termsSectionsActive, setTermsSectionsActive] = useState(true);
  const [termsSections, setTermsSections] = useState([
    { title: "", content: "" },
  ]);

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;
    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroLastUpdated(d.hero.last_updated || "");
      setHeroIntro(d.hero.intro || "");
    }
    if (d.sections?.length) setTermsSections(d.sections);
  }, [initialData]);

  /* ---------------- SYNC TO PARENT (debounced 400ms) ---------------- */
  const ref = useRef({});
  ref.current = {
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroLastUpdated,
    heroIntro,
    termsSections,
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const s = ref.current;
      setSectionsData?.({
        hero: {
          badge_text: s.heroBadgeText,
          heading: s.heroHeading,
          heading_emphasis: s.heroHeadingEmphasis,
          last_updated: s.heroLastUpdated,
          intro: s.heroIntro,
        },
        sections: s.termsSections,
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroLastUpdated,
    heroIntro,
    termsSections,
  ]);

  const updateSection = (i, val) =>
    setTermsSections((s) => s.map((x, idx) => (idx === i ? val : x)));

  /* ---------------- RENDER ---------------- */
  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Heading">
            <TextInput
              value={heroHeading}
              onChange={(e) => setHeroHeading(e.target.value)}
            />
          </Field>
          <Field label="Heading Emphasis (accent colour)">
            <TextInput
              value={heroHeadingEmphasis}
              onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Last Updated Date">
          <TextInput
            value={heroLastUpdated}
            placeholder="e.g. 1 January 2025"
            onChange={(e) => setHeroLastUpdated(e.target.value)}
          />
        </Field>
        <Field label="Introduction Paragraph">
          <TextArea
            value={heroIntro}
            rows={4}
            placeholder="Brief introduction about your terms..."
            onChange={(e) => setHeroIntro(e.target.value)}
          />
        </Field>
      </CollapsibleSection>

      {/* TERMS SECTIONS */}
      <CollapsibleSection
        title={`Terms Sections (${termsSections.length})`}
        active={termsSectionsActive}
        onToggleActive={setTermsSectionsActive}
      >
        <p className="text-xs text-muted-foreground mb-4">
          Each section renders as a heading + rich content block. Click to
          expand and edit.
        </p>

        {termsSections.map((sec, i) => (
          <TermsBlock
            key={i}
            sec={sec}
            idx={i}
            total={termsSections.length}
            onChange={(val) => updateSection(i, val)}
            onMove={(f, t) => setTermsSections(moveItem(termsSections, f, t))}
            onRemove={() =>
              setTermsSections((s) => s.filter((_, idx) => idx !== i))
            }
          />
        ))}

        <button
          onClick={() =>
            setTermsSections((s) => [...s, { title: "", content: "" }])
          }
          className="w-full flex items-center justify-center gap-2 text-xs py-3 border-2 border-dashed border-border rounded-xl hover:border-accent/40 hover:text-accent transition-colors mt-1"
        >
          <Plus size={14} /> Add Section
        </button>
      </CollapsibleSection>
    </>
  );
}
